import fileDownload from 'js-file-download';
import { BACKEND_URL_PREFIX } from '../utils/const';
import { checkAndUpdateToken } from './auth';
import {
  NETWORK_REQUEST_SENT,
  PORTFOLIO_DOCUMENT_LIST_FETCH_COMPLETED,
  PORTFOLIO_LOAD_FILES_LIST,
} from './types';
import { buildDefaultHeaders } from './helper';

/* Режим «Портфолио»: своё портфолио владельца учётной записи.

   Все методы содержимого и материалов сверяют идентификатор в адресе с учётной
   записью (иначе 403), сохранение идёт всегда в своё портфолио. Каждое действие
   возвращает результат вызывающему: { data } либо { error, status } — чтобы
   компонент показал текст отказа и не терял введённое при ошибке сохранения. */

/* Ограничения на прикрепляемый файл. Отдельного метода, который отдавал бы их,
   у сервера нет — значения повторяют серверную проверку из описания API
   (POST /portfolio/file/{mid}) и проверяются до отправки, чтобы не гнать на
   сервер файл, который заведомо будет отклонён. Сервер проверяет то же самое. */
export const PORTFOLIO_FILE_LIMITS = {
  maxBytes: 25 * 1024 * 1024,
  maxLabel: '25 МБ',
  extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'txt', 'jpg', 'jpeg', 'png'],
};

// Проверка выбранного файла до отправки: возвращает текст отказа либо null.
export const validateFile = (file) => {
  if (!file) return 'Файл не выбран.';
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  const { extensions, maxBytes, maxLabel } = PORTFOLIO_FILE_LIMITS;
  if (!file.name.includes('.') || !extensions.includes(ext)) {
    return `Недопустимый вид файла: ${ext || 'без расширения'}. Допустимые виды: ${extensions.join(', ')}.`;
  }
  if (file.size > maxBytes) {
    return `Размер файла больше ${maxLabel}.`;
  }
  return null;
};

/* Текст ошибки из ответа сервера. Тела отказов неоднородны: 400 — JSON с полем
   detail, 409 — JSON с error, 401 — строка, 500 — HTML-страница. Показываем
   только то, что предназначено пользователю; остальное заменяем своим текстом. */
const readError = async (response, fallback) => {
  let body = '';
  try {
    body = await response.text();
  } catch (e) {
    body = '';
  }
  try {
    const json = JSON.parse(body);
    if (json && typeof json.detail === 'string') return json.detail;
  } catch (e) {
    // не JSON — текст сервера пользователю не показываем
  }
  if (response.status === 401) return 'Сеанс истёк. Войдите заново.';
  if (response.status === 403) return 'Доступ к чужому портфолио закрыт.';
  if (response.status === 500) return `${fallback}: внутренняя ошибка сервера (500).`;
  return `${fallback} (код ${response.status}).`;
};

const authHeader = (getState) => `Basic ${btoa(`${getState().auth.token}:empty`)}`;

export const getInfo = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Нет авторизации', status: 401 };

  const { mid } = getState().auth;
  const response = await fetch(`${BACKEND_URL_PREFIX}/portfolio/${mid}`, buildDefaultHeaders(getState));
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (!response.ok) {
    return { error: await readError(response, 'Не удалось загрузить портфолио'), status: response.status };
  }
  const info = await response.json();
  dispatch({ type: PORTFOLIO_DOCUMENT_LIST_FETCH_COMPLETED, info });
  return { data: info };
};

/* Сохранение принимает все десять полей разом и заменяет ранее сохранённое.
   Ответ — эхо запроса без обрезки пробелов и снятия задвоенных строк, поэтому
   после успеха портфолио перечитывается методом GET. */
export const updateInfo = (values) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Нет авторизации', status: 401 };

  const response = await fetch(`${BACKEND_URL_PREFIX}/portfolio`, {
    method: 'POST',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Authorization: authHeader(getState),
    },
    body: JSON.stringify(values),
  });
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (!response.ok) {
    return { error: await readError(response, 'Не удалось сохранить портфолио'), status: response.status };
  }
  const reread = await dispatch(getInfo());
  if (reread.error) {
    // сохранено, но перечитать не удалось: показываем то, что отправили
    dispatch({ type: PORTFOLIO_DOCUMENT_LIST_FETCH_COMPLETED, info: values });
    return { data: values };
  }
  return reread;
};

export const getFiles = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Нет авторизации', status: 401 };

  const { mid } = getState().auth;
  const response = await fetch(`${BACKEND_URL_PREFIX}/portfolio/file/${mid}`, buildDefaultHeaders(getState));
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (!response.ok) {
    return { error: await readError(response, 'Не удалось загрузить перечень файлов'), status: response.status };
  }
  const files = await response.json();
  dispatch({ type: PORTFOLIO_LOAD_FILES_LIST, files: Array.isArray(files) ? files : [] });
  return { data: files };
};

// Адрес скачивания собирается из поля urlencoded записи перечня
export const fileUrl = (mid, urlencoded) => `${BACKEND_URL_PREFIX}/portfolio/file/${mid}/${urlencoded}`;

/* Скачивание: файл открывается в новой вкладке. Прямой ссылкой нельзя —
   методу нужен заголовок авторизации, поэтому файл забирается запросом,
   а вкладка открывается на blob-адрес; если браузер вкладку заблокировал —
   обычное скачивание. */
export const downloadFile = (urlencoded, name) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Нет авторизации', status: 401 };

  const { mid } = getState().auth;
  const response = await fetch(fileUrl(mid, urlencoded), buildDefaultHeaders(getState));
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (!response.ok) {
    const error = response.status === 404
      ? `Файл «${name}» в портфолио не найден.`
      : await readError(response, 'Не удалось получить файл');
    return { error, status: response.status };
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener');
  if (!win) fileDownload(blob, name);
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  return { data: true };
};

/* Загрузка одного файла. Отказы: 400 — вид или размер (текст в detail),
   409 — файл с таким именем уже прикреплён (признак fileExist, текст свой),
   200 с success:false — часть file пришла без файла. */
export const uploadFile = (file) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Нет авторизации', status: 401 };

  const { mid } = getState().auth;
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${BACKEND_URL_PREFIX}/portfolio/file/${mid}`, {
    method: 'POST',
    body: formData,
    headers: {
      'Access-Control-Allow-Origin': '*',
      Authorization: authHeader(getState),
    },
  });
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (response.status === 409) {
    return { error: `Файл «${file.name}» уже прикреплён к портфолио. Чтобы заменить его, сначала удалите прежний.`, status: 409 };
  }
  if (!response.ok) {
    return { error: await readError(response, 'Не удалось загрузить файл'), status: response.status };
  }
  let body = {};
  try {
    body = await response.json();
  } catch (e) {
    body = {};
  }
  if (body.success === false) {
    return { error: 'Файл не выбран.', status: response.status };
  }
  await dispatch(getFiles());
  return { data: true };
};

export const removeFile = (urlencoded, name) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Нет авторизации', status: 401 };

  const { mid } = getState().auth;
  const response = await fetch(fileUrl(mid, urlencoded), {
    method: 'DELETE',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Authorization: authHeader(getState),
    },
  });
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (!response.ok) {
    const error = response.status === 404
      ? `Файл «${name}» в портфолио не найден: возможно, он уже удалён.`
      : await readError(response, 'Не удалось удалить файл');
    // перечень мог разойтись с сервером — обновляем его в любом случае
    dispatch(getFiles());
    return { error, status: response.status };
  }
  await dispatch(getFiles());
  return { data: true };
};
